import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ShieldCheck, Users, ArrowRight, LogOut } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { usePanelAuth } from './PanelAuthContext';

export default function PortalSelection() {
  const { gwToken, bootChecked, logout } = usePanelAuth();

  if (bootChecked && !gwToken) return <Navigate to="/official" replace />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white px-4 py-12">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-emerald-300 text-xs uppercase tracking-widest font-semibold">Gateway · Verified ✓</p>
            <h1 className="text-3xl sm:text-4xl font-bold mt-1">Choose Your Portal</h1>
          </div>
          <Button
            variant="ghost"
            onClick={() => logout(true)}
            className="text-white/80 hover:text-white hover:bg-white/10"
            data-testid="exit-gateway"
          >
            <LogOut size={16} className="mr-1.5" /> Exit
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <PortalCard
            to="/official/admin-login"
            testid="portal-card-admin"
            icon={<ShieldCheck size={28} />}
            iconBg="from-emerald-500 to-emerald-700"
            title="Admin Portal"
            desc="Full master control — leads, customers, payments, employees, coupons, settings, analytics."
            footer="Email or mobile + password required"
          />
          <PortalCard
            to="/official/employee-login"
            testid="portal-card-employee"
            icon={<Users size={28} />}
            iconBg="from-blue-500 to-blue-700"
            title="Employee Portal"
            desc="View assigned jobs, update statuses, upload service reports & view your own profile."
            footer="Employees only — restricted access"
          />
        </div>

        <p className="text-center text-xs text-white/50 mt-10">
          Two-factor security — gateway authenticated, second-stage login pending.
        </p>
      </div>
    </div>
  );
}

function PortalCard({ to, testid, icon, iconBg, title, desc, footer }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group block bg-white/95 text-slate-900 rounded-3xl p-8 shadow-2xl hover:shadow-emerald-500/30 transition-all duration-300 hover:-translate-y-1 border border-white/20"
    >
      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${iconBg} text-white flex items-center justify-center mb-5 shadow-lg`}>
        {icon}
      </div>
      <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
      <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
        <span>{footer}</span>
        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold group-hover:translate-x-1 transition-transform">
          Continue <ArrowRight size={14} />
        </span>
      </div>
    </Link>
  );
}
